/**
 * React Query hooks for the badge achievement system.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { mlbApi } from '../services/api';
import type {
  BadgeCatalogResponse,
  UserBadgesPublicResponse,
  UserBadgesDashboardResponse,
  BadgeNotificationsListResponse,
} from '../services/api';
import { STALE_TIMES, QUERY_RETRY_CONFIG } from '../types/statcast';

export const badgeKeys = {
  all: ['badges'] as const,
  catalog: () => [...badgeKeys.all, 'catalog'] as const,
  userBadges: (userId: number) => [...badgeKeys.all, 'user', userId] as const,
  myBadges: () => [...badgeKeys.all, 'me'] as const,
  notifications: () => [...badgeKeys.all, 'notifications'] as const,
};

/** Fetch badge catalog (public, no auth). */
export function useBadgeCatalog() {
  return useQuery<BadgeCatalogResponse>({
    queryKey: badgeKeys.catalog(),
    queryFn: () => mlbApi.getBadgeCatalog(),
    staleTime: STALE_TIMES.LIVE_DATA,
    ...QUERY_RETRY_CONFIG,
  });
}

/** Fetch a user's public earned badges. */
export function useUserBadges(userId: number | undefined) {
  return useQuery<UserBadgesPublicResponse>({
    queryKey: badgeKeys.userBadges(userId!),
    queryFn: () => mlbApi.getUserBadges(userId!),
    enabled: !!userId,
    staleTime: STALE_TIMES.LIVE_DATA,
    ...QUERY_RETRY_CONFIG,
  });
}

/** Fetch authenticated user's badge dashboard (earned + progress). */
export function useMyBadges() {
  const { getToken, isSignedIn } = useAuth();

  return useQuery<UserBadgesDashboardResponse>({
    queryKey: badgeKeys.myBadges(),
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.getMyBadges(token);
    },
    enabled: !!isSignedIn,
    staleTime: STALE_TIMES.LIVE_DATA,
    ...QUERY_RETRY_CONFIG,
  });
}

/** Fetch pending badge notifications. */
export function useBadgeNotifications() {
  const { getToken, isSignedIn } = useAuth();

  return useQuery<BadgeNotificationsListResponse>({
    queryKey: badgeKeys.notifications(),
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.getBadgeNotifications(token);
    },
    enabled: !!isSignedIn,
    staleTime: 30 * 1000, // 30 seconds — check frequently for new notifications
    ...QUERY_RETRY_CONFIG,
  });
}

/** Mark all badge notifications as read. */
export function useMarkBadgeNotificationsRead() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.markBadgeNotificationsRead(token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: badgeKeys.notifications() });
    },
  });
}

/** Admin: grant a badge. */
export function useGrantBadge() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: {
      user_id: number;
      badge_key: string;
      context?: Record<string, unknown>;
    }) => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.grantBadge(body, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: badgeKeys.all });
    },
  });
}

/** Admin: revoke a badge. */
export function useRevokeBadge() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: { user_id: number; badge_key: string; season?: number }) => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.revokeBadge(body, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: badgeKeys.all });
    },
  });
}
